/**
 * "I'M NOT FREE" ON A SUB REQUEST, the read side.
 *
 * Bobbie, 2026-08-06 (DECISIONS.md, "FOUR ASKS FROM 2026-08-06", item 1):
 * "This needs a i'm not free... and should show up for Kim if no on ehas it
 * covered." Before this a teacher could only claim or stay silent, so silence
 * meant both "not free" and "have not looked", and Kim could not tell them
 * apart.
 *
 * The write goes through the server lane (netlify/functions/subClaim.mjs,
 * action 'decline', lib/subDecline.mjs), never a client write, so it costs no
 * rules bytes. The lane stores ONE entry per PERSON on the request:
 *
 *   notFree: { [personKey]: { name, uids, at } }
 *
 * `uids` is every sign-in that person holds, resolved on the server with the
 * same alias helper the claim lane uses. A Jr Staff is one person however she
 * signs in (Bobbie, 2026-09-07), so a "not free" pressed on either sign-in
 * matches both, and asking with the ONE uid this browser is signed in as is
 * already the whole answer.
 *
 * Each entry also carries the class and date it was given for. A Director or
 * the requester editing the request leaves old answers behind, and the read
 * side ignores them, so nobody is shown as "not free" for a night she was
 * never asked about.
 *
 * Pure and importless, so node runs it under --experimental-strip-types.
 */

export interface SubNotFreeEntry {
  name?: unknown;
  uids?: unknown;
  at?: unknown;
  /** The class and date she said no to. An entry for any other class or
   *  date is stale (the request was edited) and is ignored, so an edit
   *  clears every answer with no write and no rules bytes. */
  date?: unknown;
  offeringId?: unknown;
}

export type SubNotFreeMap = Record<string, SubNotFreeEntry | null | undefined>;

/** The request fields these helpers read. */
export interface SubNotFreeSource {
  notFree?: unknown;
  date?: unknown;
  offeringId?: unknown;
}

function entryUids(entry: SubNotFreeEntry | null | undefined): string[] {
  return Array.isArray(entry?.uids)
    ? entry.uids.filter((uid): uid is string => typeof uid === 'string' && uid.length > 0)
    : [];
}

/** The answers that still stand: given for THIS class on THIS date. */
function currentEntries(request: SubNotFreeSource | null | undefined): SubNotFreeEntry[] {
  const notFree = request?.notFree;
  if (!notFree || typeof notFree !== 'object' || Array.isArray(notFree)) return [];
  return Object.values(notFree as SubNotFreeMap).filter(
    (entry): entry is SubNotFreeEntry => !!entry && typeof entry === 'object'
      && entry.date === request?.date
      && entry.offeringId === request?.offeringId
  );
}

/** Did this viewer say "I'm not free" on this request, on any of her sign-ins? */
export function viewerSaidNotFree(
  request: SubNotFreeSource | null | undefined,
  viewerUids: readonly (string | null | undefined)[]
): boolean {
  const mine = viewerUids.filter((uid): uid is string => typeof uid === 'string' && uid.length > 0);
  if (mine.length === 0) return false;
  return currentEntries(request).some((entry) => entryUids(entry).some((uid) => mine.includes(uid)));
}

/** The names of everyone who said "I'm not free", one per person, in name order. */
export function notFreeNames(request: SubNotFreeSource | null | undefined): string[] {
  const names = currentEntries(request)
    .map((entry) => (typeof entry.name === 'string' ? entry.name.trim() : ''))
    .map((name) => name || 'A teammate');
  return names.sort((a, b) => a.localeCompare(b));
}

/** The one sentence the requester and a Director read on the card. Empty when nobody said it. */
export function notFreeSentence(request: SubNotFreeSource | null | undefined): string {
  const names = notFreeNames(request);
  if (names.length === 0) return '';
  return `Not free: ${names.join(', ')}.`;
}
